import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DatePicker } from "@/components/common/DatePicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useHousehold } from "@/context/HouseholdContext";
import { ItemsEditor, ShareBox, itemsTotal } from "@/components/pots/PotItems";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

export const KINDS = { gift: "Cadeau", project: "Projet de vie", event: "Événement / repas" };

export const PotFormDialog = ({ onClose, preset = {} }) => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [v, setV] = useState({ title: preset.title || "", description: preset.description || "", goal: "", end_date: "", suggested: "", hide_amounts: false, forHousehold: false, kind: preset.relative_id ? "gift" : "gift", people: "" });
  const [items, setItems] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const num = (x) => (x === "" ? null : Number(x));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/pots", { title: v.title, description: v.description || null, goal: num(v.goal), end_date: v.end_date || null, suggested: num(v.suggested),
        kind: v.kind, items: v.kind === "event" ? items : [], people: v.kind === "event" ? num(v.people) : null,
        hide_amounts: v.hide_amounts, household_id: v.forHousehold && active ? active.id : null, relative_id: preset.relative_id || null });
      if (photo) { const fd = new FormData(); fd.append("file", photo); await api.post(`/pots/${data.id}/photo`, fd).catch((err) => toast.error(formatApiError(err))); }
      qc.invalidateQueries({ queryKey: ["pots"] });
      toast.success("Cagnotte créée : partagez le lien !");
      onClose();
      navigate(`/cagnottes/${data.id}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="pot-form-dialog">
        <DialogHeader><DialogTitle>Nouvelle cagnotte</DialogTitle><DialogDescription>Un cadeau commun, un projet de vie… Les participants paient par carte, même sans compte CASHMY.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-3 gap-1.5" data-testid="pot-kind">{Object.entries(KINDS).map(([k, l]) => (
            <button type="button" key={k} onClick={() => setV({ ...v, kind: k })} className={cn("rounded-xl border px-2 py-2 text-xs font-bold transition-colors", v.kind === k ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted")} data-testid={`pot-kind-${k}`}>{l}</button>))}</div>
          <Input value={v.title} onChange={set("title")} placeholder="Titre (ex. Cadeau des 40 ans de Julie)" required minLength={3} data-testid="pot-title" />
          <Textarea value={v.description} onChange={set("description")} rows={3} maxLength={2000} placeholder="Description du projet" data-testid="pot-description" />
          {v.kind === "event" && (
            <div className="space-y-2 rounded-xl border border-dashed border-border p-3">
              <div className="text-sm font-bold">Liste des achats</div>
              <ItemsEditor items={items} onChange={setItems} />
              <Input type="number" min="1" value={v.people} onChange={set("people")} placeholder="Nombre de personnes qui participent" data-testid="pot-people" />
              <ShareBox total={itemsTotal(items)} people={Number(v.people) || null} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" min="1" value={v.goal} onChange={set("goal")} placeholder="Objectif € (facultatif)" data-testid="pot-goal" />
            <Input type="number" min="1" value={v.suggested} onChange={set("suggested")} placeholder={v.kind === "event" ? "Part calculée auto" : "Montant suggéré €"} data-testid="pot-suggested" />
          </div>
          <label className="block space-y-1 text-xs font-medium">Date de fin (facultatif)<DatePicker value={v.end_date} onChange={set("end_date")} fromDate={new Date()} data-testid="pot-end-date" /></label>
          <label className="block space-y-1 text-xs font-medium">Photo<Input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setPhoto(e.target.files[0] || null)} data-testid="pot-photo" /></label>
          <label className="flex items-center justify-between gap-3 text-sm">Masquer les montants sur la page publique<Switch checked={v.hide_amounts} onCheckedChange={(c) => setV({ ...v, hide_amounts: c })} data-testid="pot-hide-amounts" /></label>
          {active && <label className="flex items-center justify-between gap-3 text-sm">Cagnotte du foyer « {active.name} »<Switch checked={v.forHousehold} onCheckedChange={(c) => setV({ ...v, forHousehold: c })} data-testid="pot-household" /></label>}
          <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="pot-submit">Créer la cagnotte</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
